import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TESTIMONIALS } from "./TestimonialSection";

const BRAND_COLOR = "#1a3f6b";
const GRAY_DARK = "#374151";
const SECTION_BG = "#f1f5f9";
const SERIF = "Georgia, 'Times New Roman', serif";

function getInitials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(n, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

// Some agency accent colours (e.g. Point Residential's light cyan) are tuned for
// the dark TestimonialSection. On this light section they're too faint as text,
// so darken anything too bright to keep enough contrast - borders/tints keep the
// original colour, only text uses this.
function toReadable(hex) {
  let { r, g, b } = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const TARGET = 0.4;
  if (lum > 0.5) {
    const f = TARGET / lum;
    r = Math.round(r * f);
    g = Math.round(g * f);
    b = Math.round(b * f);
  }
  return `rgb(${r}, ${g}, ${b})`;
}

function Avatar({ data, size }) {
  const { profileImage, name, color } = data;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        flexShrink: 0,
        border: `2px solid ${color}`,
        background: profileImage ? "transparent" : `${color}25`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 700,
        fontSize: size > 40 ? "1rem" : "0.78rem",
      }}
    >
      {profileImage ? (
        <img src={profileImage} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        getInitials(name)
      )}
    </div>
  );
}

// Compact "proof" card - one per testimonial. Activating it features that quote.
function CompactCard({ data, active, onClick, onFocus, affordance = "below" }) {
  const { color, logo, logoBackground, name, agency, heading, snippet } = data;
  const accentText = toReadable(color);
  const baseShadow = active ? `0 12px 30px ${color}22` : "0 4px 16px rgba(0,0,0,0.05)";
  const activeLabel = affordance === "right" ? "Showing →" : "Showing below ↓";
  const idleLabel = affordance === "right" ? "Read the full quote →" : "Read the full quote ↓";
  return (
    <button
      type="button"
      onClick={onClick}
      onFocus={onFocus}
      aria-pressed={active}
      style={{
        textAlign: "left",
        font: "inherit",
        margin: 0,
        cursor: "pointer",
        appearance: "none",
        WebkitAppearance: "none",
        width: "100%",
        background: "#fff",
        border: active ? `1.5px solid ${color}` : "1px solid #e2e8f0",
        borderRadius: "16px",
        padding: "1.5rem",
        boxShadow: baseShadow,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 14px 34px rgba(0,0,0,0.12)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = baseShadow;
      }}
    >
      {/* Agency logo */}
      <div style={{ height: "30px", marginBottom: "1rem", display: "flex", alignItems: "center" }}>
        <img
          src={logo}
          alt={agency}
          style={{ height: "26px", maxWidth: "150px", objectFit: "contain", objectPosition: "left", display: "block", background: logoBackground || "transparent", borderRadius: "4px" }}
        />
      </div>
      {/* Title */}
      <h3 style={{ margin: "0 0 0.6rem", fontSize: "1.08rem", fontWeight: 700, color: BRAND_COLOR, lineHeight: 1.3 }}>
        {heading}
      </h3>
      {/* Snippet */}
      <p style={{ margin: 0, fontSize: "0.92rem", color: GRAY_DARK, lineHeight: 1.55, fontStyle: "italic", flex: 1 }}>
        &ldquo;{snippet}&rdquo;
      </p>
      {/* Author */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "1.25rem" }}>
        <Avatar data={data} size={36} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: BRAND_COLOR }}>{name}</div>
          <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{agency}</div>
        </div>
      </div>
      {/* Click affordance */}
      <div style={{ marginTop: "1rem", paddingTop: "0.9rem", borderTop: "1px solid #f1f5f9", color: accentText, fontSize: "0.8rem", fontWeight: 700 }}>
        {active ? activeLabel : idleLabel}
      </div>
    </button>
  );
}

// The selected testimonial - full quote, shown large.
// `stacked` lays identity above the quote (used in the narrow desktop right column);
// otherwise identity sits to the left of the quote.
function FeaturedTestimonial({ data, isMobile, stacked = false, margin, dense = false }) {
  const { color, logo, logoBackground, name, title, agency, pills = [], quote } = data;
  const accentText = toReadable(color);
  const vertical = isMobile || stacked;
  // Desktop stacked layout: tighten the identity block so "Featured story" reads compact.
  // `dense` further compresses the mobile modal so the full quote fits on screen.
  const compact = (stacked && !isMobile) || dense;
  return (
    <div
      style={{
        marginTop: margin != null ? margin : isMobile ? "1.25rem" : "1.5rem",
        background: "#fff",
        border: dense ? "none" : "1px solid #e2e8f0",
        borderRadius: dense ? 0 : "20px",
        boxShadow: dense ? "none" : "0 20px 50px rgba(0,0,0,0.08)",
        overflow: "hidden",
        minHeight: dense ? "100%" : undefined,
        display: "flex",
        flexDirection: vertical ? "column" : "row",
      }}
    >
      {/* Left: identity */}
      <div
        style={{
          flex: vertical ? "none" : "0 0 34%",
          background: "#f8fafc",
          borderRight: vertical ? "none" : "1px solid #eef2f6",
          borderBottom: vertical ? "1px solid #eef2f6" : "none",
          padding: dense ? "1.25rem 1.25rem 1rem" : isMobile ? "1.75rem" : compact ? "1.5rem 1.75rem" : "2.25rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: logoBackground || "#fff",
            borderRadius: "10px",
            padding: "0.6rem 0.9rem",
            display: "inline-flex",
            alignSelf: "flex-start",
            border: "1px solid #eef2f6",
          }}
        >
          <img src={logo} alt={agency} style={{ height: "30px", maxWidth: "150px", objectFit: "contain", display: "block" }} />
        </div>

        <div style={{ marginTop: compact ? "1rem" : vertical ? "1.5rem" : "auto" }}>
          <span style={{ display: "block", color: accentText, fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: compact ? "0.6rem" : "0.9rem" }}>
            Featured story
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: compact ? "0.85rem" : "1.1rem" }}>
            <Avatar data={data} size={compact ? 44 : 52} />
            <div>
              <div style={{ fontSize: "0.95rem", fontWeight: 700, color: BRAND_COLOR }}>{name}</div>
              <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{title}</div>
              <div style={{ fontSize: "0.8rem", color: accentText, fontWeight: 600 }}>{agency}</div>
            </div>
          </div>
          {pills.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
              {pills.map((p) => (
                <span
                  key={p}
                  style={{
                    background: `${color}1F`,
                    color: accentText,
                    border: `1px solid ${color}55`,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    borderRadius: "999px",
                    padding: "0.25rem 0.7rem",
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: quote */}
      <div style={{ flex: 1, padding: dense ? "1.1rem 1.25rem 1.5rem" : isMobile ? "1.75rem" : "2rem 2.5rem" }}>
        <div style={{ fontSize: dense ? "2rem" : "2.75rem", lineHeight: 1, color, opacity: 0.25, fontFamily: SERIF, marginBottom: dense ? "-0.4rem" : "-0.6rem", userSelect: "none" }}>
          &ldquo;
        </div>
        <p style={{ fontSize: dense ? "0.95rem" : isMobile ? "1.05rem" : "0.98rem", lineHeight: dense ? 1.55 : 1.6, color: "#1f2937", fontStyle: "italic", whiteSpace: "pre-line", margin: 0 }}>
          {quote}
        </p>
      </div>
    </div>
  );
}

// Mobile slide: just the interesting bits - heading, the one-line highlight, and
// pills - kept short so the carousel doesn't feel crowded. The full quote opens
// in a modal.
function MobileQuoteCard({ data, onReadFull }) {
  const { color, logo, logoBackground, name, title, agency, heading, snippet, pills = [] } = data;
  const accentText = toReadable(color);
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        boxShadow: "0 12px 34px rgba(0,0,0,0.07)",
        padding: "1.75rem",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Logo */}
      <div
        style={{
          background: logoBackground || "#fff",
          borderRadius: "10px",
          padding: "0.5rem 0.8rem",
          display: "inline-flex",
          alignSelf: "flex-start",
          border: "1px solid #eef2f6",
          marginBottom: "1.1rem",
        }}
      >
        <img src={logo} alt={agency} style={{ height: "26px", maxWidth: "140px", objectFit: "contain", display: "block" }} />
      </div>

      {/* Heading */}
      <h3 style={{ margin: "0 0 0.85rem", fontSize: "1.1rem", fontWeight: 700, color: BRAND_COLOR, lineHeight: 1.3 }}>
        {heading}
      </h3>

      {/* The highlight - the most interesting line, big and quoted */}
      <p style={{ margin: 0, fontSize: "1.15rem", lineHeight: 1.55, color: "#1f2937", fontStyle: "italic", fontFamily: SERIF }}>
        &ldquo;{snippet}&rdquo;
      </p>

      {/* Pills */}
      {pills.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "1.1rem" }}>
          {pills.map((p) => (
            <span
              key={p}
              style={{
                background: `${color}1F`,
                color: accentText,
                border: `1px solid ${color}55`,
                fontSize: "0.72rem",
                fontWeight: 600,
                borderRadius: "999px",
                padding: "0.25rem 0.7rem",
              }}
            >
              {p}
            </span>
          ))}
        </div>
      )}

      {/* Author */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "auto", paddingTop: "1.5rem" }}>
        <Avatar data={data} size={40} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "0.88rem", fontWeight: 700, color: BRAND_COLOR }}>{name}</div>
          <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{title}, {agency}</div>
        </div>
      </div>

      {/* Read full */}
      <button
        type="button"
        onClick={onReadFull}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
          marginTop: "1.25rem",
          width: "100%",
          background: BRAND_COLOR,
          color: "#fff",
          border: "none",
          borderRadius: "12px",
          padding: "0.8rem 1rem",
          fontSize: "0.9rem",
          fontWeight: 700,
        }}
      >
        Read full testimonial →
      </button>
    </div>
  );
}

// Full-quote overlay opened from a mobile slide.
function TestimonialModal({ data, onClose }) {
  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Rendered through a portal to document.body so it escapes the section's
  // stacking context and truly sits above the top nav and bottom banner.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        // Above the top nav and bottom banner (navbar/mobile menu sit at 2000).
        zIndex: 3000,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          flex: 1,
          width: "100%",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "fixed",
            top: "0.75rem",
            right: "0.75rem",
            appearance: "none",
            WebkitAppearance: "none",
            cursor: "pointer",
            border: "none",
            background: "#fff",
            color: BRAND_COLOR,
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            fontSize: "1.2rem",
            fontWeight: 700,
            boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            zIndex: 1,
          }}
        >
          ×
        </button>
        <FeaturedTestimonial data={data} isMobile dense margin={0} />
      </div>
    </div>,
    document.body
  );
}

// Mobile: a swipeable carousel of short highlight cards, scroll-snapped, with dots
// that track and drive the position. Tapping a card opens the full quote.
function MobileCarousel({ order }) {
  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [openKey, setOpenKey] = useState(null);

  // Track which slide is centred as the user swipes.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScroll = () => {
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let nearest = 0;
      let best = Infinity;
      slideRefs.current.forEach((el, i) => {
        if (!el) return;
        const mid = el.offsetLeft + el.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < best) {
          best = dist;
          nearest = i;
        }
      });
      setActive(nearest);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const el = slideRefs.current[i];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  return (
    <div style={{ marginTop: "2.5rem" }}>
      <div
        ref={scrollerRef}
        style={{
          display: "flex",
          gap: "1rem",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          // Breathing room so the first/last slide can centre.
          padding: "0.5rem 1.5rem",
          margin: "0 -1.5rem",
        }}
      >
        {order.map((key, i) => (
          <div
            key={key}
            ref={(el) => (slideRefs.current[i] = el)}
            style={{ flex: "0 0 85%", scrollSnapAlign: "center", display: "flex" }}
          >
            <MobileQuoteCard data={TESTIMONIALS[key]} onReadFull={() => setOpenKey(key)} />
          </div>
        ))}
      </div>

      {/* Dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginTop: "1.25rem" }}>
        {order.map((key, i) => (
          <button
            key={key}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            aria-current={i === active}
            style={{
              appearance: "none",
              WebkitAppearance: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              width: i === active ? "24px" : "8px",
              height: "8px",
              borderRadius: "999px",
              background: i === active ? BRAND_COLOR : "#cbd5e1",
              transition: "width 0.2s, background 0.2s",
            }}
          />
        ))}
      </div>

      {openKey && <TestimonialModal data={TESTIMONIALS[openKey]} onClose={() => setOpenKey(null)} />}
    </div>
  );
}

export default function TestimonialsShowcase({
  isMobile,
  featured = "ashley",
  order = ["ashley", "liam", "barbara"],
  seeMoreHref = "/testimonials",
}) {
  const [activeKey, setActiveKey] = useState(featured);

  const featuredData = TESTIMONIALS[activeKey] || TESTIMONIALS[order[0]];

  return (
    <section style={{ background: SECTION_BG, padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem", position: "relative", overflow: "hidden" }}>
      {/* Faint grid framing (echoes the editorial look) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />
      <div style={{ maxWidth: "1140px", margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? "2rem" : "2.8rem", fontWeight: 800, color: BRAND_COLOR, letterSpacing: "-0.02em", lineHeight: 1.15, margin: "0 0 1rem" }}>
            <span style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 600 }}>Loved</span> by agents across Australia
          </h2>
          <p style={{ fontSize: "1.1rem", color: GRAY_DARK, lineHeight: 1.6, margin: 0 }}>
            Vendorleads helps agencies of every size win more listings, with less effort.
          </p>
        </div>

        {isMobile ? (
          // Mobile: swipeable carousel.
          <MobileCarousel order={order} />
        ) : (
          // Desktop: stacked cards on the left, featured quote on the right.
          // Tabbing (or clicking) a card features it - focus selects, so you can
          // move through them with the keyboard.
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
              gap: "1.5rem",
              alignItems: "start",
              marginTop: "3rem",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {order.map((key) => (
                <CompactCard
                  key={key}
                  data={TESTIMONIALS[key]}
                  active={key === activeKey}
                  affordance="right"
                  onClick={() => setActiveKey(key)}
                  onFocus={() => setActiveKey(key)}
                />
              ))}
            </div>
            <FeaturedTestimonial data={featuredData} stacked margin={0} />
          </div>
        )}

        {/* Still reachable: the full reviews page */}
        <div style={{ textAlign: "center", marginTop: "1.75rem" }}>
          <Link to={seeMoreHref} style={{ color: BRAND_COLOR, fontWeight: 600, fontSize: "0.9rem", textDecoration: "none" }}>
            See all reviews →
          </Link>
        </div>
      </div>
    </section>
  );
}
